/**
 * The HenniPenny NILM workbench page.
 *
 * The whole field is data-driven: the ENERGY_NILM manifest supplies every tab's
 * copy + structure, the shared WorkbenchShell renders it, and useNilmWorkbench
 * injects the four rich panels (disaggregation run · precision/recall/F1 ·
 * confusion matrix · provenance) as per-tab trailing slots. Metrics are computed
 * deterministically in the browser — no backend, no research service call.
 */
import { useState } from 'react';
import { FlaskConical } from 'lucide-react';
import { WorkbenchShell } from '@/features/workbench/WorkbenchShell';
import { ENERGY_NILM_WORKBENCH } from '@/features/workbench/manifests/energy-nilm.workbench';
import { useNilmWorkbench } from '@/features/workbench/nilm/NilmWorkbench';

const panelStyle = {
  border: '1px solid var(--sp-border)',
  borderRadius: 'var(--sp-radius-lg)',
  background: 'var(--sp-bg-elev)',
  padding: 'var(--sp-space-4)',
};

export function WorkbenchPage() {
  const manifest = ENERGY_NILM_WORKBENCH;
  const [activeTab, setActiveTab] = useState<string>(manifest.tabs[0]?.id ?? '');
  const nilm = useNilmWorkbench();

  // HenniPenny/NILM rich panels, keyed by the manifest's tab ids and injected into
  // the shared shell. The manifest's card panels render above these trailing slots.
  const nilmTabSlots = {
    disaggregation: { trailing: nilm.disaggregation },
    metrics: { trailing: nilm.metrics },
    confusion: { trailing: nilm.confusion },
    provenance: { trailing: nilm.provenance },
  };

  const openTab = (tab: string) => {
    setActiveTab(tab);
    window.requestAnimationFrame(() => {
      document.getElementById('workbench-tabs')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-space-4)' }}>
      <section style={panelStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 260 }}>
          <FlaskConical size={20} style={{ color: 'var(--sp-text-muted)', marginTop: 2, flexShrink: 0 }} />
          <div>
            <div className="sp-eyebrow">{manifest.eyebrow}</div>
            <h1 className="sp-display" style={{ margin: '6px 0 0', fontSize: 26, fontWeight: 600 }}>
              {manifest.branding?.title ?? manifest.displayName}
            </h1>
            <p style={{ margin: '10px 0 0', color: 'var(--sp-text-muted)', maxWidth: 820, lineHeight: 1.65, fontSize: 14 }}>
              Score appliance-level load disaggregation against a pinned benchmark. The workbench parses your CSVs
              and computes precision, recall, F1 and the confusion matrix{' '}
              <strong style={{ color: 'var(--sp-text)' }}>deterministically in the browser</strong> — the same math
              the sealed <code>evidence.v1</code> run certifies, not a second source of truth. It scores the metrics,
              never the model. Load the synthetic sample on the first tab to see it end to end.
            </p>
          </div>
        </div>
      </section>

      <WorkbenchShell
        manifest={manifest}
        activeTab={activeTab}
        onTabChange={openTab}
        tabSlots={nilmTabSlots}
      />
    </div>
  );
}
